import React, { useCallback, useMemo, useState } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Dropdown } from "primereact/dropdown";
import { DataTableStateEvent } from "primereact/datatable";

import SectionFrame from "./components/SectionFrame";
import MonthsSelector from "./components/MonthsSelector";
import KpiTiles from "./components/KpiTiles";
import OverdueAlertsPanel from "./components/OverdueAlertsPanel";
import FunnelChart from "./components/FunnelChart";
import TimePerStepChart from "./components/TimePerStepChart";
import DurationTrendChart from "./components/DurationTrendChart";
import SignMethodPanel from "./components/SignMethodPanel";
import {
  CustomerTypePanel,
  LeadSourcePanel,
  VendorPanel,
} from "./components/SegmentationPanels";
import VolumeChart from "./components/VolumeChart";
import SatisfactionSection from "./components/SatisfactionSection";
import ProspectAnalyticsTable from "./components/ProspectAnalyticsTable";
import ProspectTableFilters, {
  EMPTY_FILTERS,
  isRangeInverted,
  ProspectFilters,
} from "./components/ProspectTableFilters";
import { downloadProspectCsv } from "./components/exportProspects";

import { useAnalyticsSection } from "./hooks/useOnboardingAnalytics";
import {
  getCustomerTypeSegmentation,
  getDashboardKpis,
  getDurationTrend,
  getFunnel,
  getLeadSourceSegmentation,
  getOverdueAlerts,
  getProspectAnalyticsList,
  getSatisfaction,
  getSignMethodBreakdown,
  getTimePerStep,
  getVendorSegmentation,
  getVolume,
} from "../../../services/onboarding/analytics";
import { ProspectSortColumn } from "../../../types/onboardingAnalytics";
import { ProspectTypeFilter } from "../../../types/onboarding";
import { ANALYTICS_TYPE_OPTIONS } from "../../../utils/prospectType";
import "./OnboardingAnalytics.css";

const PAGE_SIZE = 20;

const TAB_OVERVIEW = 0;
const TAB_PROSPECTS = 1;
const TAB_TIME = 2;
const TAB_SEGMENTS = 3;

/**
 * The onboarding analytics dashboard (CWA-159).
 *
 * The page is the composition root: it owns every request and hands plain data
 * to components that never fetch. Each section loads and fails on its own, so
 * one broken endpoint costs one panel rather than the screen (FR-034).
 *
 * Sections behind a tab stay unfetched until that tab is first opened — eleven
 * endpoints each scanning three tables in full is not something to spend on
 * panels nobody is looking at.
 */
const OnboardingAnalyticsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(TAB_OVERVIEW);
  const [refreshKey, setRefreshKey] = useState(0);

  // Once a tab has been opened its sections stay live, so returning to it does
  // not refetch and switching away does not discard what was loaded.
  const [seenTabs, setSeenTabs] = useState<Set<number>>(
    () => new Set([TAB_OVERVIEW])
  );
  const seen = useCallback((tab: number) => seenTabs.has(tab), [seenTabs]);

  const [trendMonths, setTrendMonths] = useState(6);
  const [volumeMonths, setVolumeMonths] = useState(6);
  const [satisfactionMonths, setSatisfactionMonths] = useState(6);
  // CWA-184: every section counts one kind of onboarding; new customers by default, so an owner adding a
  // station doesn't change the "new customers" figures.
  const [prospectType, setProspectType] = useState<ProspectTypeFilter>("NEW_CUSTOMER");

  const [filters, setFilters] = useState<ProspectFilters>(EMPTY_FILTERS);
  const [first, setFirst] = useState(0);
  const [sortField, setSortField] = useState<ProspectSortColumn>("created_at");
  const [sortOrder, setSortOrder] = useState<1 | -1>(-1);

  const rangeInverted = isRangeInverted(filters);

  const handleTabChange = (e: { index: number }) => {
    setActiveTab(e.index);
    setSeenTabs((prev) =>
      prev.has(e.index) ? prev : new Set(prev).add(e.index)
    );
  };

  // ---- Overview ----
  const kpis = useAnalyticsSection(() => getDashboardKpis(prospectType), {
    refreshKey,
    deps: [prospectType],
  });
  const alerts = useAnalyticsSection(() => getOverdueAlerts(prospectType), {
    refreshKey,
    deps: [prospectType],
  });
  const funnel = useAnalyticsSection(() => getFunnel(prospectType), {
    refreshKey,
    deps: [prospectType],
  });
  const satisfaction = useAnalyticsSection(
    () => getSatisfaction(satisfactionMonths, prospectType),
    { refreshKey, deps: [satisfactionMonths, prospectType] }
  );

  // ---- Prospects ----
  // The query is rebuilt whenever a filter, page or sort changes; the hook
  // re-requests on that change and on nothing else. An inverted date range is
  // never sent — it would earn a 400 whose message we would have to hide.
  const prospectQuery = useMemo(
    () => ({
      page: Math.floor(first / PAGE_SIZE) + 1,
      size: PAGE_SIZE,
      sort: sortField,
      order: (sortOrder === 1 ? "asc" : "desc") as "asc" | "desc",
      states: filters.states,
      customerType: filters.customerType as any,
      signMethod: filters.signMethod as any,
      leadSource: filters.leadSource,
      vendor: filters.vendor,
      dateFrom: filters.dateFrom ? filters.dateFrom.toISOString() : null,
      dateTo: filters.dateTo ? filters.dateTo.toISOString() : null,
      dateField: filters.dateField,
      setupFeeStatus: filters.setupFeeStatus as any,
      prospectType,
    }),
    [first, sortField, sortOrder, filters, prospectType]
  );

  const prospects = useAnalyticsSection(
    () => getProspectAnalyticsList(prospectQuery),
    {
      enabled: seen(TAB_PROSPECTS) && !rangeInverted,
      refreshKey,
      deps: [prospectQuery],
    }
  );

  // ---- Time & performance ----
  const timePerStep = useAnalyticsSection(() => getTimePerStep(prospectType), {
    enabled: seen(TAB_TIME),
    refreshKey,
    deps: [prospectType],
  });
  const durationTrend = useAnalyticsSection(
    () => getDurationTrend(trendMonths, prospectType),
    { enabled: seen(TAB_TIME), refreshKey, deps: [trendMonths, prospectType] }
  );
  const signMethod = useAnalyticsSection(
    () => getSignMethodBreakdown(prospectType),
    { enabled: seen(TAB_TIME), refreshKey, deps: [prospectType] }
  );

  // ---- Segments & volume ----
  const leadSource = useAnalyticsSection(
    () => getLeadSourceSegmentation(prospectType),
    { enabled: seen(TAB_SEGMENTS), refreshKey, deps: [prospectType] }
  );
  const customerType = useAnalyticsSection(
    () => getCustomerTypeSegmentation(prospectType),
    { enabled: seen(TAB_SEGMENTS), refreshKey, deps: [prospectType] }
  );
  const vendor = useAnalyticsSection(
    () => getVendorSegmentation(prospectType),
    { enabled: seen(TAB_SEGMENTS), refreshKey, deps: [prospectType] }
  );
  const volume = useAnalyticsSection(
    () => getVolume(volumeMonths, prospectType),
    { enabled: seen(TAB_SEGMENTS), refreshKey, deps: [volumeMonths, prospectType] }
  );

  const vendorOptions = useMemo(
    () => (vendor.data ?? []).map((v) => v.vendor),
    [vendor.data]
  );

  const handlePage = (e: DataTableStateEvent) => setFirst(e.first ?? 0);

  const handleSort = (e: DataTableStateEvent) => {
    setSortField((e.sortField as ProspectSortColumn) ?? "created_at");
    setSortOrder(e.sortOrder === 1 ? 1 : -1);
    setFirst(0);
  };

  const handleFilters = (next: ProspectFilters) => {
    setFilters(next);
    setFirst(0);
  };

  return (
    <div className="onboarding-analytics p-3">
      <div className="flex flex-column lg:flex-row lg:align-items-center lg:justify-content-between gap-3 mb-4">
        <div>
          <h2 className="m-0">Onboarding Analytics</h2>
          <p className="mt-1 mb-0 text-500 text-sm">
            Figures are read live when this page loads — use Refresh to bring
            them up to date.
          </p>
        </div>
        <div className="flex flex-wrap align-items-center gap-2">
          <Dropdown
            value={prospectType}
            options={ANALYTICS_TYPE_OPTIONS}
            onChange={(e) => {
              setProspectType(e.value as ProspectTypeFilter);
              setFirst(0);
            }}
            aria-label="Onboarding type"
            className="w-full sm:w-14rem"
          />
          <Button
            label="Refresh"
            icon="pi pi-refresh"
            outlined
            onClick={() => setRefreshKey((k) => k + 1)}
          />
        </div>
      </div>

      <TabView activeIndex={activeTab} onTabChange={handleTabChange}>
        <TabPanel header="Overview">
          <div className="grid">
            <div className="col-12">
              <SectionFrame
                title="Pipeline at a glance"
                loading={kpis.loading}
                error={kpis.error}
                onRetry={kpis.retry}
                isEmpty={!kpis.data}
                emptyMessage="No prospects have been recorded yet."
              >
                {kpis.data && <KpiTiles kpis={kpis.data} />}
              </SectionFrame>
            </div>

            <div className="col-12 xl:col-6">
              <SectionFrame
                title="Needs attention"
                caveat="Prospects past the waiting time allowed for their stage. Overdue prospects only — reminders and failed notifications aren't reported here."
                loading={alerts.loading}
                error={alerts.error}
                onRetry={alerts.retry}
                isEmpty={!alerts.data?.length}
                emptyMessage="Nothing is overdue. Every prospect is within the time allowed for its stage."
              >
                {alerts.data && <OverdueAlertsPanel alerts={alerts.data} />}
              </SectionFrame>
            </div>

            <div className="col-12 xl:col-6">
              <SectionFrame
                title="Pipeline funnel"
                caveat="How many prospects reached each milestone, and how many were lost on the way to the next."
                loading={funnel.loading}
                error={funnel.error}
                onRetry={funnel.retry}
                isEmpty={!funnel.data?.length}
                emptyMessage="No prospects to chart yet."
              >
                {funnel.data && <FunnelChart stages={funnel.data} />}
              </SectionFrame>
            </div>

            <div className="col-12">
              <SectionFrame
                title="Onboarding satisfaction"
                caveat="How customers rated their onboarding, for the onboardings completed in the selected period."
                loading={satisfaction.loading}
                error={satisfaction.error}
                onRetry={satisfaction.retry}
                isEmpty={!satisfaction.data?.ratedCount}
                emptyMessage="No ratings for onboardings completed in this period."
                right={
                  <MonthsSelector
                    value={satisfactionMonths}
                    onChange={setSatisfactionMonths}
                    ariaLabel="Satisfaction range"
                  />
                }
              >
                {satisfaction.data && (
                  <SatisfactionSection satisfaction={satisfaction.data} />
                )}
              </SectionFrame>
            </div>
          </div>
        </TabPanel>

        <TabPanel header="Prospects">
          <SectionFrame
            title="All prospects"
            loading={prospects.loading}
            error={prospects.error}
            onRetry={prospects.retry}
          >
            <ProspectTableFilters
              filters={filters}
              onChange={handleFilters}
              vendorOptions={vendorOptions}
            />

            {rangeInverted && (
              <Message
                severity="warn"
                className="mb-3"
                text="The end of the date range is before its start. Adjust the dates to see results."
              />
            )}

            {!rangeInverted && prospects.data && (
              <ProspectAnalyticsTable
                page={prospects.data}
                rows={PAGE_SIZE}
                first={first}
                sortField={sortField}
                sortOrder={sortOrder}
                onPage={handlePage}
                onSort={handleSort}
                onExport={() => downloadProspectCsv(prospects.data!.prospects)}
              />
            )}
          </SectionFrame>
        </TabPanel>

        <TabPanel header="Time & performance">
          <div className="grid">
            <div className="col-12 xl:col-6">
              <SectionFrame
                title="Time per step"
                caveat="Recorded consecutive steps only — a journey that skips a stage isn't counted, so the signing steps may be absent."
                loading={timePerStep.loading}
                error={timePerStep.error}
                onRetry={timePerStep.retry}
                isEmpty={!timePerStep.data?.length}
                emptyMessage="No step timings recorded yet."
              >
                {timePerStep.data && (
                  <TimePerStepChart steps={timePerStep.data} />
                )}
              </SectionFrame>
            </div>

            <div className="col-12 xl:col-6">
              <SectionFrame
                title="Time to sign"
                caveat="From the contract being sent to it being signed."
                loading={signMethod.loading}
                error={signMethod.error}
                onRetry={signMethod.retry}
                isEmpty={!signMethod.data?.length}
                emptyMessage="No contracts signed yet."
              >
                {signMethod.data && <SignMethodPanel methods={signMethod.data} />}
              </SectionFrame>
            </div>

            <div className="col-12">
              <SectionFrame
                title="Onboarding duration over time"
                loading={durationTrend.loading}
                error={durationTrend.error}
                onRetry={durationTrend.retry}
                isEmpty={!durationTrend.data?.length}
                emptyMessage="No completed onboardings in this period."
                right={
                  <MonthsSelector
                    value={trendMonths}
                    onChange={setTrendMonths}
                    ariaLabel="Duration trend range"
                  />
                }
              >
                {durationTrend.data && (
                  <DurationTrendChart months={durationTrend.data} />
                )}
              </SectionFrame>
            </div>
          </div>
        </TabPanel>

        <TabPanel header="Segments & volume">
          <div className="grid">
            <div className="col-12 xl:col-6">
              <SectionFrame
                title="Where prospects come from"
                loading={leadSource.loading}
                error={leadSource.error}
                onRetry={leadSource.retry}
                isEmpty={!leadSource.data?.length}
                emptyMessage="No lead sources recorded yet."
              >
                {leadSource.data && <LeadSourcePanel segments={leadSource.data} />}
              </SectionFrame>
            </div>

            <div className="col-12 xl:col-6">
              <SectionFrame
                title="Private vs business"
                loading={customerType.loading}
                error={customerType.error}
                onRetry={customerType.retry}
                isEmpty={!customerType.data}
                emptyMessage="No prospects recorded yet."
              >
                {customerType.data && (
                  <CustomerTypePanel segmentation={customerType.data} />
                )}
              </SectionFrame>
            </div>

            <div className="col-12 xl:col-6">
              <SectionFrame
                title="Setup time by vendor"
                caveat="From the configuration email going out to the station being public."
                loading={vendor.loading}
                error={vendor.error}
                onRetry={vendor.retry}
                isEmpty={!vendor.data?.length}
                emptyMessage="No vendor data yet."
              >
                {vendor.data && <VendorPanel vendors={vendor.data} />}
              </SectionFrame>
            </div>

            <div className="col-12 xl:col-6">
              <SectionFrame
                title="Volume over time"
                loading={volume.loading}
                error={volume.error}
                onRetry={volume.retry}
                isEmpty={!volume.data?.length}
                emptyMessage="No volume recorded in this period."
                right={
                  <MonthsSelector
                    value={volumeMonths}
                    onChange={setVolumeMonths}
                    ariaLabel="Volume range"
                  />
                }
              >
                {volume.data && <VolumeChart months={volume.data} />}
              </SectionFrame>
            </div>
          </div>
        </TabPanel>
      </TabView>
    </div>
  );
};

export default OnboardingAnalyticsPage;
